import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { fetchCurriculum, reorderActivity, deleteActivity } from '../api/curriculum';

export const useCurriculum = (childId) => {
  return useQuery({
    queryKey: ['curriculum', childId],
    queryFn: () => fetchCurriculum(childId),
    enabled: !!childId
  });
};

export const useReorderActivity = (childId) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ activityId, prevActivityId, nextActivityId }) =>
      reorderActivity(childId, activityId, prevActivityId, nextActivityId),
    onMutate: async ({ activityId, prevActivityId, nextActivityId }) => {
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({ queryKey: ['curriculum', childId] });

      // Snapshot the previous value
      const previousData = queryClient.getQueryData(['curriculum', childId]);

      // Optimistically update the order
      queryClient.setQueryData(['curriculum', childId], (old) => {
        if (!old) return old;

        const activities = [...old.activities];
        const activityIndex = activities.findIndex(a => a.id === activityId);
        if (activityIndex === -1) return old;

        const [activity] = activities.splice(activityIndex, 1);

        // Find the new position
        let newIndex;
        if (prevActivityId === null) {
          newIndex = 0;
        } else if (nextActivityId === null) {
          newIndex = activities.length;
        } else {
          const prevIndex = activities.findIndex(a => a.id === prevActivityId);
          newIndex = prevIndex + 1;
        }

        activities.splice(newIndex, 0, activity);

        return {
          ...old,
          activities
        };
      });

      return { previousData };
    },
    onError: (err, variables, context) => {
      // If the mutation fails, use the context returned from onMutate to roll back
      if (context?.previousData) {
        queryClient.setQueryData(['curriculum', childId], context.previousData);
      }
    },
    onSettled: () => {
      // Refetch after error or success to ensure data is in sync
      queryClient.invalidateQueries({ queryKey: ['curriculum', childId] });
    }
  });
};

export const useDeleteActivity = (childId) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (activityId) => deleteActivity(childId, activityId),
    onMutate: async (activityId) => {
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({ queryKey: ['curriculum', childId] });

      // Snapshot the previous value
      const previousData = queryClient.getQueryData(['curriculum', childId]);

      // Optimistically remove the activity
      queryClient.setQueryData(['curriculum', childId], (old) => {
        if (!old) return old;

        return {
          ...old,
          activities: old.activities.filter(a => a.id !== activityId),
          child: {
            ...old.child,
            activities_count: old.child.activities_count - 1
          }
        };
      });

      return { previousData };
    },
    onError: (err, variables, context) => {
      // If the mutation fails, use the context returned from onMutate to roll back
      if (context?.previousData) {
        queryClient.setQueryData(['curriculum', childId], context.previousData);
      }
    },
    onSettled: () => {
      // Refetch after error or success to ensure data is in sync
      queryClient.invalidateQueries({ queryKey: ['curriculum', childId] });
    }
  });
};
